// src/components/CheckOutInvoice.jsx
import React, { forwardRef, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import jsPDF from "jspdf";
import "jspdf-autotable";
import axiosInstance from "../api/axiosInstance";
import { API_BASE_URL } from "../config/apiConfig";
import { useAuth } from "../context/AuthContext";

import "./CheckOutInvoice.css";

/** Normalize relative -> absolute (for API/Static file URLs) */
const toAbs = (u) => {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  const base = (API_BASE_URL || "").replace(/\/+$/, "");
  return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
};

/** Read a Blob into a data URL (PNG/JPEG supported) */
async function blobToDataUrl(blob) {
  return await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

/** Fetch image via Axios (respects auth/cookies), return data URL */
async function fetchLogoDataUrl(url) {
  if (!url) return null;
  
  try {
    ////console.log("🔄 Fetching logo from:", url);
    
    // Add cache busting and ensure it's a valid URL
    const cacheBusterUrl = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
    
    const resp = await axiosInstance.get(cacheBusterUrl, {
      responseType: 'blob',
      withCredentials: true,
      timeout: 15000,
      headers: {
        'Accept': 'image/png,image/jpeg,image/*,*/*',
        'Cache-Control': 'no-cache'
      }
    });
    
    if (!resp.data || resp.data.size === 0) {
      console.warn("❌ Empty logo blob received");
      return null;
    }
    
    //console.log("✅ Logo blob received, type:", resp.data.type, "size:", resp.data.size);
    
    // Convert to data URL
    const dataUrl = await blobToDataUrl(resp.data);
    return dataUrl;
    
  } catch (error) {
    console.warn("❌ Logo fetch failed, trying fallback:", error);
    return await loadImageDirectly(url);
  }
}

/** Fallback method using canvas */
async function loadImageDirectly(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        // White background for transparent images
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        const dataUrl = canvas.toDataURL('image/png');
        //console.log("✅ Fallback logo loaded via canvas");
        resolve(dataUrl);
      } catch (canvasError) {
        console.warn("Canvas conversion failed:", canvasError);
        resolve(null);
      }
    };
    img.onerror = () => {
      console.warn("Direct image loading failed");
      resolve(null);
    };
    img.src = url;
  });
}
/** DEBUG function to test logo data URL */
async function testLogoDataUrl(logoDataUrl) {
  return new Promise((resolve) => {
    if (!logoDataUrl) {
      resolve(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      //console.log("✅ Logo test passed - valid image:", img.width, "x", img.height);
      resolve(true);
    };
    img.onerror = () => {
      //console.log("❌ Logo test failed - invalid image data");
      resolve(false);
    };
    img.src = logoDataUrl;
  });
}
/** Optimize PNG for PDF - resize and compress */
async function optimizePngForPdf(dataUrl, maxWidth = 150) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = function() {
      try {
        // Calculate new dimensions maintaining aspect ratio
        const ratio = Math.min(maxWidth / img.width, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        // Draw with white background for transparent PNGs
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        resolve(canvas.toDataURL('image/png', 0.9)); // Compressed PNG
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error("Failed to load image for optimization"));
    img.src = dataUrl;
  });
}

/** Convert PNG to JPEG as fallback */
/** Convert PNG to JPEG as fallback */
async function convertPngToJpeg(dataUrl) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = function() {
      try {
        canvas.width = img.width;
        canvas.height = img.height;
        
        // White background for JPEG
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error("Failed to load image for JPEG conversion"));
    img.src = dataUrl;
  });
}


/** Robust getter for logo from hotelDetails prop (various shapes) */
function getHotelLogoUrlFromProps(hotelDetails) {
  const raw =
    hotelDetails?.logoUrl ??
    hotelDetails?.LogoUrl ??
    hotelDetails?.logoURL ??
    hotelDetails?.logo ??
    null;
  return raw ? raw : null;
}

/** Generate PDF and return as Blob for preview */
/** Generate PDF and return as Blob for preview */
async function generatePDFBlob({ logoDataUrl, hotelName, hotelAddress, hotelPhone, invoiceDate, bookingNo, name, roomNumber, checkInDate, checkOutDate, currency, basePrice, vatPct, vatAmtPerNight, levies, computedNights, stayTotal, items, itemsTotal, totalPaid, grandTotal, balanceDue }) {
  const doc = new jsPDF("p", "pt", "a4");
  const pw = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  //console.log("🎯 Starting PDF generation...");
  //console.log("📊 Logo data URL present:", !!logoDataUrl);

  // DEBUG: Test the logo data URL before using it
  if (logoDataUrl) {
    const isLogoValid = await testLogoDataUrl(logoDataUrl);
    //console.log("🔍 Logo validity check:", isLogoValid);
    
    if (!isLogoValid) {
      console.warn("⚠️ Invalid logo data URL, skipping logo");
      logoDataUrl = null;
    }
  }

  // Header: logo with enhanced error handling
  let logoAdded = false;
  let logoHeight = 0;
  
  if (logoDataUrl) {
    try {
      //console.log("🖼️ Attempting to add logo to PDF...");
      
      // More robust image loading with timeout
      await new Promise((resolve, reject) => {
        const testImg = new Image();
        const timeout = setTimeout(() => {
          reject(new Error("Logo loading timeout"));
        }, 5000);
        
        testImg.onload = () => {
          clearTimeout(timeout);
          //console.log("✅ Logo loaded successfully, dimensions:", testImg.width, "x", testImg.height);
          resolve();
        };
        testImg.onerror = () => {
          clearTimeout(timeout);
          console.error("❌ Logo failed to load in test");
          reject(new Error("Logo failed to load"));
        };
        testImg.src = logoDataUrl;
      });

      // Get image properties
      let imgProps;
      try {
        imgProps = doc.getImageProperties(logoDataUrl);
        //console.log("📐 Image properties:", imgProps);
      } catch (propsError) {
        console.warn("Could not get image properties:", propsError);
        // Use default dimensions
        imgProps = { width: 100, height: 100 };
      }
      
      // Calculate dimensions to maintain aspect ratio
      const maxWidth = 80;
      const maxHeight = 80;
      const ratio = Math.min(maxWidth / imgProps.width, maxHeight / imgProps.height);
      const width = Math.max(imgProps.width * ratio, 50); // Minimum 50px
      logoHeight = Math.max(imgProps.height * ratio, 50);
      
      //console.log("📏 Logo dimensions for PDF:", width, "x", logoHeight);

      // Try different image formats with better error handling
      const formats = [
        { type: "PNG", method: () => doc.addImage(logoDataUrl, "PNG", margin, y, width, logoHeight) },
        { type: "JPEG", method: async () => {
          const jpegDataUrl = await convertPngToJpeg(logoDataUrl);
          doc.addImage(jpegDataUrl, "JPEG", margin, y, width, logoHeight);
        }},
        { type: "AUTO", method: () => doc.addImage(logoDataUrl, margin, y, width, logoHeight) }
      ];

      for (const format of formats) {
        try {
          if (format.type === "JPEG") {
            await format.method();
          } else {
            format.method();
          }
          logoAdded = true;
          //console.log(`✅ Logo added successfully as ${format.type}`);
          break;
        } catch (formatError) {
          console.warn(`❌ ${format.type} format failed:`, formatError.message);
          continue;
        }
      }

    } catch (imageError) {
      console.warn("❌ All logo embedding attempts failed:", imageError);
      logoAdded = false;
      logoHeight = 0;
    }
  } else {
    console.warn("📝 No logo data URL available for PDF");
  }

  // Hotel info - position based on whether logo was added
  const infoX = logoAdded ? margin + 100 : margin;
  const infoStartY = logoAdded ? y + (logoHeight / 2) - 10 : y; // Center hotel info vertically with logo
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(hotelName, infoX, infoStartY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(hotelAddress, infoX, infoStartY + 20);
  doc.text(`Contact: ${hotelPhone}`, infoX, infoStartY + 35);

  // Calculate Y position for next section - ensure proper spacing
  if (logoAdded) {
    // Use the bottom of the logo plus some padding
    y = Math.max(y + logoHeight, infoStartY + 45) + 25; // Extra space after logo/hotel info
  } else {
    // No logo, use hotel info bottom plus padding
    y = infoStartY + 45 + 25;
  }

  // Title + meta - WITH PROPER SPACING
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("CHECK-OUT INVOICE", margin, y);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  y += 30; // Increased space after title

  const leftCol = margin;
  const rightCol = pw - margin - 150;
  doc.text(`Date: ${invoiceDate.toLocaleDateString()}`, leftCol, y);
  doc.text(`Invoice #: ${bookingNo}`, rightCol, y);
  y += 15;
  doc.text(`Guest: ${name}`, leftCol, y);
  doc.text(`Room: ${roomNumber}`, rightCol, y);
  y += 25;

  // Bill To + Property blocks
  const boxWidth = (pw - margin * 3) / 2;

  // Bill To
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("BILL TO", margin, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Name: ${name}`, margin, y + 15);

  let billToY = y + 15;
  if (checkInDate) {
    billToY += 12;
    doc.text(`Check-In: ${checkInDate.toLocaleDateString()}`, margin, billToY);
  }
  if (checkOutDate) {
    billToY += 12;
    doc.text(`Check-Out: ${checkOutDate.toLocaleDateString()}`, margin, billToY);
  }

  // Property
  const propertyX = margin + boxWidth + margin;
  doc.setFont("helvetica", "bold");
  doc.text("PROPERTY", propertyX, y);
  doc.setFont("helvetica", "normal");
  doc.text(hotelName, propertyX, y + 15);
  doc.text(hotelAddress, propertyX, y + 27);
  doc.text(`Contact: ${hotelPhone}`, propertyX, y + 39);

  y = Math.max(billToY, y + 45) + 25; // Increased space before table

  // Stay Summary
  doc.autoTable({
    startY: y,
    theme: "grid",
    styles: { 
      fontSize: 10, 
      cellPadding: 6, 
      lineColor: [0, 0, 0], 
      lineWidth: 0.1,
      font: "helvetica"
    },
    headStyles: { 
      fillColor: [245, 245, 245], 
      textColor: [0, 0, 0], 
      fontStyle: "bold",
      fontSize: 10
    },
    bodyStyles: {
      fontSize: 10
    },
    head: [["Description", "Amount"]],
    body: [
      ["Base (per night)", `${currency}${basePrice.toFixed(2)}`],
      [`VAT (${vatPct}%)`, `${currency}${vatAmtPerNight.toFixed(2)}`],
      ["Levies (per night)", `${currency}${levies.toFixed(2)}`],
      ["Nights", `${computedNights}`],
      [
        { 
          content: "Stay Total", 
          styles: { fontStyle: "bold", fontSize: 11 } 
        },
        { 
          content: `${currency}${stayTotal.toFixed(2)}`, 
          styles: { fontStyle: "bold", fontSize: 11 } 
        },
      ],
    ],
    columnStyles: { 
      0: { cellWidth: "auto", fontStyle: "normal" }, 
      1: { cellWidth: 100, halign: "right", fontStyle: "normal" } 
    },
    margin: { left: margin, right: margin },
  });
  
  let nextY = doc.lastAutoTable.finalY + 20; // Increased space after table

  // Extras
  if (items && items.length > 0) {
    doc.autoTable({
      startY: nextY,
      theme: "grid",
      styles: { 
        fontSize: 10, 
        cellPadding: 6, 
        lineColor: [0, 0, 0], 
        lineWidth: 0.1,
        font: "helvetica"
      },
      headStyles: { 
        fillColor: [245, 245, 245], 
        textColor: [0, 0, 0], 
        fontStyle: "bold",
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 10
      },
      head: [["Extras", "Amount"]],
      body: items.map((b, index) => [
        b.description || `Item ${index + 1}`,
        `${currency}${Number(b.amount || 0).toFixed(2)}`,
      ]),
      columnStyles: { 
        0: { cellWidth: "auto", fontStyle: "normal" }, 
        1: { cellWidth: 100, halign: "right", fontStyle: "normal" } 
      },
      margin: { left: margin, right: margin },
    });

    // Extras subtotal
    doc.autoTable({
      startY: doc.lastAutoTable.finalY,
      theme: "grid",
      styles: { 
        fontSize: 10, 
        cellPadding: 6, 
        lineColor: [0, 0, 0], 
        lineWidth: 0.1,
        font: "helvetica"
      },
      bodyStyles: {
        fontSize: 10
      },
      body: [
        [
          { 
            content: "Extras Subtotal", 
            styles: { fontStyle: "bold", fontSize: 11 } 
          },
          {
            content: `${currency}${itemsTotal.toFixed(2)}`,
            styles: { fontStyle: "bold", fontSize: 11, halign: "right" },
          },
        ],
      ],
      columnStyles: { 
        0: { cellWidth: "auto" }, 
        1: { cellWidth: 100, halign: "right" } 
      },
      margin: { left: margin, right: margin },
    });

    nextY = doc.lastAutoTable.finalY + 20; // Increased space
  }

  // Totals section
  doc.autoTable({
    startY: nextY,
    theme: "grid",
    styles: { 
      fontSize: 11, 
      cellPadding: 8, 
      lineColor: [0, 0, 0], 
      lineWidth: 0.1,
      font: "helvetica"
    },
    bodyStyles: {
      fontSize: 11,
      font: "helvetica"
    },
    body: [
      [
        "Total Paid", 
        `${currency}${totalPaid.toFixed(2)}`
      ],
      [
        "Grand Total", 
        `${currency}${grandTotal.toFixed(2)}`
      ],
      [
        { 
          content: "Balance Due", 
          styles: { fontStyle: "bold", fontSize: 12 } 
        },
        {
          content: `${currency}${balanceDue.toFixed(2)}`,
          styles: { fontStyle: "bold", fontSize: 12, halign: "right" },
        },
      ],
    ],
    columnStyles: {
      0: { 
        cellWidth: "auto", 
        fontStyle: "bold",
        fontSize: 11
      },
      1: { 
        cellWidth: 120, 
        halign: "right", 
        fontStyle: "bold",
        fontSize: 11
      },
    },
    margin: { left: pw - margin - 250, right: margin },
  });

  const finalY = doc.lastAutoTable.finalY + 25; // Increased space before footer

  // Footer
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.text("Thank you for staying with us!", margin, finalY);

  // Add page border (optional)
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(1);
  doc.rect(margin - 10, margin - 10, pw - (margin * 2) + 20, doc.internal.pageSize.getHeight() - (margin * 2) + 20);

  //console.log("✅ PDF generation completed successfully");
  //console.log("📊 Logo was added:", logoAdded);
  
  // Return as Blob for preview
  return doc.output('blob');
}




const CheckOutInvoice = forwardRef(function CheckOutInvoice(
  { isOpen, onClose, hotelDetails, guestDetails, billingRecords },
  ref
) {
  const { authData } = useAuth();

  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Load hotel logo and pre-process for PDF
 // Load hotel logo and pre-process for PDF
useEffect(() => {
  const fetchAndPrepareLogo = async () => {
    if (!isOpen) return;

    setLogoLoading(true);
    setLogoDataUrl(null);

    try {
      let finalLogoUrl = null;
      const hotelId = authData?.hotelId;

      // Strategy 1: Dedicated logo endpoint
      if (hotelId) {
        try {
          const r = await axiosInstance.get(`/Hotel/${hotelId}/logo`);
          const raw = r?.data?.logoUrl ?? r?.data?.LogoUrl;
          if (raw) {
            // API already returns absolute URL - use it directly
            finalLogoUrl = raw;
            //console.log("✅ Using logo from dedicated endpoint:", finalLogoUrl);
          }
        } catch (error) {
          console.warn("Dedicated logo endpoint failed:", error);
        }
      }

      // Strategy 2: Hotel object endpoint
      if (!finalLogoUrl && hotelId) {
        try {
          const r2 = await axiosInstance.get(`/Hotel/${hotelId}`);
          const raw2 = r2?.data?.logoUrl ?? r2?.data?.LogoUrl ?? r2?.data?.logoURL;
          if (raw2) {
            // Use directly - API should return absolute URL
            finalLogoUrl = raw2;
            //console.log("✅ Using logo from hotel object:", finalLogoUrl);
          }
        } catch (error) {
          console.warn("Hotel object endpoint failed:", error);
        }
      }

      // Strategy 3: From props (apply toAbs only if needed)
      if (!finalLogoUrl) {
        const propLogo = getHotelLogoUrlFromProps(hotelDetails);
        if (propLogo) {
          // Only convert to absolute if it's a relative path
          finalLogoUrl = toAbs(propLogo);
          //console.log("✅ Using logo from props:", finalLogoUrl);
        }
      }

      // Set URL for display
      setLogoUrl(finalLogoUrl);

      // Pre-load and optimize for PDF
      if (finalLogoUrl) {
        //console.log("🔄 Pre-loading logo for PDF:", finalLogoUrl);
        const dataUrl = await fetchLogoDataUrl(finalLogoUrl);
        
        if (dataUrl) {
          try {
            // Test if it's a valid image
            await new Promise((resolve, reject) => {
              const img = new Image();
              img.onload = resolve;
              img.onerror = reject;
              img.src = dataUrl;
            });
            
            // Optimize for PDF
            const optimizedDataUrl = await optimizePngForPdf(dataUrl, 120);
            setLogoDataUrl(optimizedDataUrl);
            //console.log("✅ Logo pre-loaded and optimized for PDF");
          } catch (optimizeError) {
            console.warn("Logo optimization failed, using original:", optimizeError);
            setLogoDataUrl(dataUrl);
          }
        } else {
          console.warn("❌ Failed to fetch logo data URL");
        }
      } else {
        console.warn("❌ No logo URL found from any source");
      }
    } catch (error) {
      console.error("Logo preparation failed:", error);
    } finally {
      setLogoLoading(false);
    }
  };

  fetchAndPrepareLogo();
}, [authData, hotelDetails, isOpen]);

  // Clean up preview URL when component unmounts or closes
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  // ---------- Values & math ----------
  const currency = guestDetails?.currency || "BWP";
  const bookingNo = guestDetails?.bookingNo || guestDetails?.BookingNo || "N/A";
  const name = guestDetails?.name || "Guest";
  const roomNumber = guestDetails?.roomNumber || "—";

  const checkInDate = guestDetails?.checkInDate
    ? new Date(guestDetails.checkInDate)
    : null;
  const checkOutDate = guestDetails?.checkOutDate
    ? new Date(guestDetails.checkOutDate)
    : null;

  const computedNights = useMemo(() => {
    const apiN = Number(guestDetails?.nights);
    if (apiN && apiN > 0) return apiN;
    if (checkInDate && checkOutDate) {
      const diff = Math.ceil(
        (checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      return Math.max(diff, 1);
    }
    return 1;
  }, [guestDetails?.nights, checkInDate, checkOutDate]);

  const basePrice = Number(guestDetails?.basePrice || 0);
  const vatPct = Number(guestDetails?.vatPercentage || 0);
  const levies = Number(guestDetails?.additionalLevies || 0);

  const vatAmtPerNight = basePrice * (vatPct / 100);
  const stayCalcTotal =
    (basePrice + vatAmtPerNight + levies) * computedNights;

  // prefer API total if present
  const stayTotal =
    guestDetails?.totalAmount != null
      ? Number(guestDetails.totalAmount)
      : stayCalcTotal;

  const items = Array.isArray(billingRecords) ? billingRecords : [];
  const itemsTotal = items.reduce((s, r) => s + Number(r?.amount || 0), 0);

  const depositPaid = Number(guestDetails?.paymentAmount || 0);
  const itemsPaid = items.reduce((s, r) => s + Number(r?.paidAmount || 0), 0);
  const totalPaid = depositPaid + itemsPaid;

  const grandTotal = stayTotal + itemsTotal;
  const balanceDue = Math.max(grandTotal - totalPaid, 0);

  const hotelName =
    hotelDetails?.hotelName || hotelDetails?.name || "Hotel Name";
  const hotelAddress = hotelDetails?.address || "Hotel Address";
  const hotelPhone =
    hotelDetails?.phoneNumber || hotelDetails?.phone || "+000 000 0000";
  const invoiceDate = guestDetails?.invoiceDate
    ? new Date(guestDetails.invoiceDate)
    : new Date();

  // ---------- PDF Functions ----------
  const generatePreviewPDF = async () => {
    if (isGeneratingPreview) return;
    
    setIsGeneratingPreview(true);
    try {
      //console.log("🎯 Generating PDF preview...");
      
      const pdfBlob = await generatePDFBlob({
        logoDataUrl,
        hotelName,
        hotelAddress,
        hotelPhone,
        invoiceDate,
        bookingNo,
        name,
        roomNumber,
        checkInDate,
        checkOutDate,
        currency,
        basePrice,
        vatPct,
        vatAmtPerNight,
        levies,
        computedNights,
        stayTotal,
        items,
        itemsTotal,
        totalPaid,
        grandTotal,
        balanceDue
      });

      // Create object URL for preview
      const previewUrl = URL.createObjectURL(pdfBlob);
      
      // Clean up previous preview URL
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
      
      setPdfPreviewUrl(previewUrl);
      setShowPreview(true);
      
    } catch (error) {
      console.error("❌ PDF preview generation failed:", error);
      alert("PDF preview generation failed: " + error.message);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      //console.log("🎯 Generating PDF for download...");
      
      const pdfBlob = await generatePDFBlob({
        logoDataUrl,
        hotelName,
        hotelAddress,
        hotelPhone,
        invoiceDate,
        bookingNo,
        name,
        roomNumber,
        checkInDate,
        checkOutDate,
        currency,
        basePrice,
        vatPct,
        vatAmtPerNight,
        levies,
        computedNights,
        stayTotal,
        items,
        itemsTotal,
        totalPaid,
        grandTotal,
        balanceDue
      });

      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${bookingNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      //console.log("✅ PDF download initiated");
    } catch (error) {
      console.error("❌ PDF download failed:", error);
      alert("PDF download failed: " + error.message);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Invoice Modal */}
      <div className="invoice-overlay">
        <div className="invoice-card" ref={ref}>
          {/* Header */}
          <div className="inv-header">
            {logoUrl && !logoLoading ? (
              <img className="inv-logo" src={logoUrl} alt="Hotel Logo" />
            ) : (
              <div className="inv-logo placeholder">
                {(hotelName && hotelName[0]) || "H"}
              </div>
            )}
            <div className="inv-hotel">
              <div className="inv-hotel-name">{hotelName}</div>
              <div className="inv-hotel-sub">{hotelAddress}</div>
              <div className="inv-hotel-sub">Contact: {hotelPhone}</div>
            </div>
          </div>

          {/* Meta */}
          <div className="inv-meta">
            <div>
              <div className="inv-title">CHECK-OUT INVOICE</div>
              <div className="inv-sub">
                Date: {new Date(invoiceDate).toLocaleDateString()}
              </div>
            </div>
            <div className="inv-meta-right">
              <div className="inv-sub">Invoice #: {bookingNo}</div>
              <div className="inv-sub">Room: {roomNumber}</div>
            </div>
          </div>

          {/* Two cols */}
          <div className="inv-two-col">
            <div className="inv-box">
              <div className="inv-box-title">BILL TO</div>
              <div className="inv-line">
                <b>Name:</b> {name}
              </div>
              {checkInDate && (
                <div className="inv-line">
                  <b>Check-In:</b> {checkInDate.toLocaleDateString()}
                </div>
              )}
              {checkOutDate && (
                <div className="inv-line">
                  <b>Check-Out:</b> {checkOutDate.toLocaleDateString()}
                </div>
              )}
            </div>
            <div className="inv-box">
              <div className="inv-box-title">PROPERTY</div>
              <div className="inv-line">{hotelName}</div>
              <div className="inv-line">{hotelAddress}</div>
              <div className="inv-line">Contact: {hotelPhone}</div>
            </div>
          </div>

          {/* Stay summary */}
          <table className="inv-table">
            <tbody>
              <tr>
                <td>Base (per night)</td>
                <td className="amt">
                  {currency}
                  {basePrice.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td>VAT ({vatPct}%)</td>
                <td className="amt">
                  {currency}
                  {vatAmtPerNight.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td>Levies (per night)</td>
                <td className="amt">
                  {currency}
                  {levies.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td>Nights</td>
                <td className="amt">{computedNights}</td>
              </tr>
              <tr className="bold">
                <td>Stay Total</td>
                <td className="amt">
                  {currency}
                  {stayTotal.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Extras */}
          {items.length > 0 && (
            <>
              <div className="section-title">Extras</div>
              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="amt">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((b, idx) => (
                    <tr key={String(b.billingId ?? b.description ?? idx)}>
                      <td>{b.description || "Item"}</td>
                      <td className="amt">
                        {currency}
                        {Number(b.amount || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bold">
                    <td>Extras Subtotal</td>
                    <td className="amt">
                      {currency}
                      {itemsTotal.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {/* Totals */}
          <table className="inv-table totals">
            <tbody>
              <tr>
                <td>Total Paid</td>
                <td className="amt">
                  {currency}
                  {totalPaid.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td>Grand Total</td>
                <td className="amt">
                  {currency}
                  {grandTotal.toFixed(2)}
                </td>
              </tr>
              <tr className="bold big">
                <td>Balance Due</td>
                <td className="amt">
                  {currency}
                  {balanceDue.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="inv-footer">Thank you for staying with us!</div>

          {/* Compact Icon Buttons */}
          <div className="inv-actions-compact">
            <button 
              className="btn-icon preview" 
              onClick={generatePreviewPDF}
              disabled={isGeneratingPreview}
              title="Preview PDF"
            >
              {isGeneratingPreview ? "⏳" : "👁️"}
            </button>
            <button 
              className="btn-icon download" 
              onClick={handleDownloadPDF}
              title="Download PDF"
            >
              📥
            </button>
            <button 
              className="btn-icon close" 
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPreview && (
        <div className="pdf-preview-overlay">
          <div className="pdf-preview-modal">
            <div className="pdf-preview-header">
              <h3>PDF Preview - Invoice #{bookingNo}</h3>
              <button className="close-btn" onClick={closePreview}>×</button>
            </div>
            <div className="pdf-preview-content">
              {pdfPreviewUrl ? (
                <iframe
                  src={pdfPreviewUrl}
                  title="PDF Preview"
                  width="100%"
                  height="600px"
                  style={{ border: 'none' }}
                />
              ) : (
                <div className="pdf-loading">Loading PDF preview...</div>
              )}
            </div>
            <div className="pdf-preview-actions-compact">
              <button 
                className="btn-icon download" 
                onClick={handleDownloadPDF}
                title="Download PDF"
              >
                📥
              </button>
              <button 
                className="btn-icon close" 
                onClick={closePreview}
                title="Close Preview"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

CheckOutInvoice.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  hotelDetails: PropTypes.shape({
    hotelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hotelID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    logoUrl: PropTypes.string,
    LogoUrl: PropTypes.string,
    logoURL: PropTypes.string,
    logo: PropTypes.string,
    hotelName: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
    phoneNumber: PropTypes.string,
    phone: PropTypes.string,
  }),
  guestDetails: PropTypes.shape({
    hotelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    bookingNo: PropTypes.string,
    BookingNo: PropTypes.string,
    name: PropTypes.string,
    roomNumber: PropTypes.string,
    checkInDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    checkOutDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    nights: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    basePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vatPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    additionalLevies: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    invoiceDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
  }),
  billingRecords: PropTypes.arrayOf(
    PropTypes.shape({
      billingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      description: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      paidAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
};

export default CheckOutInvoice;